import React, { useState, useMemo } from "react";
import { DocumentRecord, EstadoDocumento, InventoryItem } from "@/hooks/useDocuments";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription, DrawerFooter, DrawerClose } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, Lock, AlertCircle, Package, Scale, Box, Boxes, AlertTriangle } from "lucide-react";
import { calcHeaderTotals } from "@/hooks/useInvoiceMath";
import { cn } from "@/lib/utils";
import { EmbarqueSelectField } from "@/components/EmbarqueSelectField";
import { isExpenseType, hasInventoryItems } from "@/lib/documentTypes";
import { ExtractedExpensesSection } from "@/components/audit/ExtractedExpensesSection";

const ESTADOS: EstadoDocumento[] = ["Pendiente", "Procesando", "Pagado", "Completado", "Cancelado"];

import { MASTER_DOC_TYPES } from "@/lib/documentTypes";

/** Options shown in the Select — excludes "Otro" which is rendered separately */
const TIPO_DOCUMENTO_OPTIONS = MASTER_DOC_TYPES.filter(t => t !== "Otro");

/** Mercancía: tipos que generan items de inventario */
const MERCHANDISE_TYPES = ["Factura Internacional", "Packing List"] as const;
/** Servicio: tipos que generan gastos prorrateables (no inventario) */
const SERVICE_TYPES = [
  "BL", "Guía Aérea", "Documento de Puerto",
  "Póliza de Seguro", "Factura de Compras",
  "Declaración de Importación", "Declaración Andina de Valor",
  "Certificado de Origen", "Certificado Técnico", "Orden de Compra",
] as const;
const isMerchandise = (t: string) => (MERCHANDISE_TYPES as readonly string[]).includes(t);
const isService = (t: string) => (SERVICE_TYPES as readonly string[]).includes(t);

type FieldDef = { key: string; label: string; type?: "number" };

/** Fields ordered to match table columns, tagged by profile */
const ALL_FIELDS: (FieldDef & { profiles: ("financial" | "packing" | "common")[] })[] = [
  // Common fields (same order as table: Fecha, No Documento, Emisor, Receptor)
  { key: "fecha", label: "Fecha", profiles: ["common"] },
  { key: "no_documento", label: "No. Documento", profiles: ["common"] },
  { key: "emisor", label: "Emisor", profiles: ["common"] },
  { key: "receptor", label: "Receptor", profiles: ["common"] },
  // Financial-only fields (Moneda, Flete, Impuestos, Descuento)
  { key: "moneda", label: "Moneda", profiles: ["financial"] },
  { key: "costo_flete", label: "Costo Flete", type: "number", profiles: ["financial"] },
  { key: "seguro", label: "Seguro", type: "number", profiles: ["financial"] },
  { key: "impuestos", label: "Impuestos", type: "number", profiles: ["financial"] },
  { key: "descuento_ajuste", label: "Descuento / Ajuste", type: "number", profiles: ["financial"] },
  { key: "otros_cargos", label: "Otros Cargos", type: "number", profiles: ["financial"] },
  // Shared location fields
  { key: "origen", label: "Origen", profiles: ["common"] },
  { key: "destino", label: "Destino", profiles: ["common"] },
  // Financial-only
  { key: "incoterm", label: "Incoterm", profiles: ["financial"] },
  // Notes
  { key: "notas_tecnicas", label: "Notas Técnicas", profiles: ["common"] },
];

/** Locked fields also depend on profile */
const ALL_LOCKED: (FieldDef & { profiles: ("financial" | "packing" | "common")[] })[] = [
  { key: "total_factura", label: "Total Factura", profiles: ["financial"] },
  { key: "total_piezas", label: "Total Piezas", profiles: ["packing", "common"] },
  { key: "peso_bruto_kg", label: "Peso Bruto (KG)", profiles: ["packing", "common"] },
];

function getProfile(documentType?: string): "financial" | "packing" {
  if (documentType?.toLowerCase() === "packing list") return "packing";
  return "financial";
}

interface Props {
  doc: DocumentRecord | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  inventoryItems?: InventoryItem[];
  documentType?: string;
}

export function EditDocumentDrawer({ doc, open, onOpenChange, inventoryItems = [], documentType }: Props) {
  const [values, setValues] = useState<Record<string, string>>({});
  const [estado, setEstado] = useState<string>("");
  const [embarqueId, setEmbarqueId] = useState<string>("");
  const [tipoDocumento, setTipoDocumento] = useState<string>("");
  const [tipoOtroText, setTipoOtroText] = useState<string>("");
  const [saving, setSaving] = useState(false);
  const [showConversionDialog, setShowConversionDialog] = useState(false);
  const [pendingTipo, setPendingTipo] = useState<string>("");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { user, orgId } = useAuth();

  const profile = getProfile(documentType || doc?.tipo_documento || "");
  const resolvedTipo = documentType || doc?.tipo_documento || "";
  const showInventorySection = hasInventoryItems(resolvedTipo);
  const showExpensesSection = isExpenseType(resolvedTipo);

  // ── Logística: bloqueo SOLO para Packing List con items ──
  const docSiblings = useMemo(
    () => (doc ? inventoryItems.filter(i => i.documento_id === doc.id) : []),
    [doc, inventoryItems]
  );
  const isPackingList = (resolvedTipo || "").toLowerCase() === "packing list";
  const logisticsLocked = isPackingList && docSiblings.length > 0;

  const editableFields = useMemo(() =>
    ALL_FIELDS.filter(f => f.profiles.includes("common") || f.profiles.includes(profile)),
    [profile]
  );

  const lockedFields = useMemo(() =>
    ALL_LOCKED.filter(f => f.profiles.includes("common") || f.profiles.includes(profile)),
    [profile]
  );

  const initValues = (d: DocumentRecord) => {
    const v: Record<string, string> = {};
    for (const f of ALL_FIELDS) {
      v[f.key] = String((d as any)[f.key] ?? "");
    }
    // Logística: preservar null como string vacío (NO "0") para distinguir "sin dato" de "cero declarado"
    const logKeys = ["peso_bruto_kg", "peso_neto_kg", "volumen_cbm", "total_piezas"] as const;
    for (const k of logKeys) {
      const raw = (d as any)[k];
      v[k] = raw == null ? "" : String(raw);
    }
    setValues(v);
    setEstado(d.estado || "Pendiente");
    setEmbarqueId((d as any).embarque_id || "");
    const tipo = d.tipo_documento || "";
    if ((TIPO_DOCUMENTO_OPTIONS as readonly string[]).includes(tipo)) {
      setTipoDocumento(tipo);
      setTipoOtroText("");
    } else if (tipo) {
      setTipoDocumento("Otro");
      setTipoOtroText(tipo);
    } else {
      setTipoDocumento("");
      setTipoOtroText("");
    }
  };

  React.useEffect(() => {
    if (open && doc) initValues(doc);
  }, [open, doc]);

  const derivedTotals = useMemo(() => {
    if (!doc) return { total_factura: 0, total_piezas: 0, peso_bruto_kg: 0 };
    const siblings = inventoryItems.filter(i => i.documento_id === doc.id);
    const flete = parseFloat(values["costo_flete"]) || 0;
    const seg = parseFloat(values["seguro"]) || 0;
    const imp = parseFloat(values["impuestos"]) || 0;
    const desc = parseFloat(values["descuento_ajuste"]) || 0;
    const otros = parseFloat(values["otros_cargos"]) || 0;
    return calcHeaderTotals(siblings, flete, imp, desc, seg, otros);
  }, [doc, values, inventoryItems]);

  const monedaValue = (values["moneda"] ?? "").trim();
  const isMonedaMissing = monedaValue.length === 0;

  /** Convierte input de logística a number | null. Vacío → null (sin dato), "0" → 0 (cero declarado). */
  const parseLogisticsInput = (raw: string): number | null => {
    const t = (raw ?? "").trim();
    if (t === "") return null;
    const n = parseFloat(t);
    if (!Number.isFinite(n) || n < 0) return null;
    return n;
  };

  const pesoBrutoNum = parseLogisticsInput(values["peso_bruto_kg"] ?? "");
  const pesoNetoNum = parseLogisticsInput(values["peso_neto_kg"] ?? "");
  const netoMayorQueBruto =
    pesoNetoNum != null && pesoBrutoNum != null && pesoNetoNum > pesoBrutoNum;

  const handleSaveClick = () => {
    if (!doc || isMonedaMissing) return;
    const oldTipo = doc.tipo_documento || "";
    const newTipo = tipoDocumento === "Otro" ? (tipoOtroText.trim() || "Otro") : tipoDocumento;
    const hasLiveItems = inventoryItems.some(i => i.documento_id === doc.id);
    if (isMerchandise(oldTipo) && isService(newTipo) && hasLiveItems) {
      setPendingTipo(newTipo);
      setShowConversionDialog(true);
      return;
    }
    performSave(false);
  };

  const performSave = async (convertItems: boolean) => {
    if (!doc) return;
    setSaving(true);

    // 1. Convertir items → gastos PRIMERO si el usuario confirmó
    if (convertItems && user?.id && orgId) {
      const { error: rpcError } = await supabase.rpc("convert_document_items_to_gastos", {
        p_document_id: doc.id,
        p_user_id: user.id,
        p_org_id: orgId,
      });
      if (rpcError) {
        setSaving(false);
        toast({ title: "Error al convertir items", description: rpcError.message, variant: "destructive" });
        return;
      }
    }

    // 2. UPDATE registro_documentos
    const resolvedTipo = tipoDocumento === "Otro" ? (tipoOtroText.trim() || "Otro") : tipoDocumento;
    const update: Record<string, any> = { estado, embarque_id: embarqueId || null, tipo_documento: resolvedTipo || null };
    for (const f of editableFields) {
      if (f.type === "number") {
        update[f.key] = parseFloat(values[f.key]) || 0;
      } else {
        update[f.key] = values[f.key] || null;
      }
    }
    update["total_factura"] = derivedTotals.total_factura;

    if (logisticsLocked) {
      update["peso_bruto_kg"] = derivedTotals.peso_bruto_kg;
      update["total_piezas"] = derivedTotals.total_piezas;
      update["peso_neto_kg"] = parseLogisticsInput(values["peso_neto_kg"] ?? "");
      update["volumen_cbm"] = parseLogisticsInput(values["volumen_cbm"] ?? "");
    } else {
      update["peso_bruto_kg"] = parseLogisticsInput(values["peso_bruto_kg"] ?? "");
      update["peso_neto_kg"] = parseLogisticsInput(values["peso_neto_kg"] ?? "");
      update["volumen_cbm"] = parseLogisticsInput(values["volumen_cbm"] ?? "");
      update["total_piezas"] = parseLogisticsInput(values["total_piezas"] ?? "");
    }

    const { error } = await supabase
      .from("registro_documentos")
      .update(update)
      .eq("id", doc.id);

    setSaving(false);
    if (error) {
      toast({ title: "Error al guardar", description: error.message, variant: "destructive" });
      return;
    }

    toast({
      title: convertItems
        ? "✅ Documento actualizado y items convertidos a gastos"
        : "✅ Documento actualizado"
    });
    queryClient.invalidateQueries({ queryKey: ["documents"] });
    queryClient.invalidateQueries({ queryKey: ["all-documents"] });
    queryClient.invalidateQueries({ queryKey: ["document-types"] });
    queryClient.invalidateQueries({ queryKey: ["embarques"] });
    queryClient.invalidateQueries({ queryKey: ["embarque-documents"] });
    queryClient.invalidateQueries({ queryKey: ["embarque-detail"] });
    queryClient.invalidateQueries({ queryKey: ["inventory"] });
    queryClient.invalidateQueries({ queryKey: ["gastos-embarque"] });
    queryClient.invalidateQueries({ queryKey: ["shipment-landed-cost"] });
    onOpenChange(false);
  };

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[90vh]">
        <DrawerHeader>
          <DrawerTitle>Editar Documento</DrawerTitle>
          <DrawerDescription>{doc?.no_documento}</DrawerDescription>
        </DrawerHeader>
        <div className="px-4 pb-4 overflow-y-auto space-y-4">
          {/* Alerta prominente de moneda faltante — siempre evaluada */}
          {isMonedaMissing ? (
            <Alert key="moneda-alert" variant="destructive" className="animate-in fade-in slide-in-from-top-2 duration-300 border-destructive/60 bg-destructive/10">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle className="font-semibold">Acción Requerida: Datos Financieros Incompletos</AlertTitle>
              <AlertDescription>
                No se puede procesar el costeo ni cerrar el embarque porque la moneda no fue detectada. Por favor, asígnela manualmente para habilitar el guardado.
              </AlertDescription>
            </Alert>
          ) : null}

          {/* Estado */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Estado</Label>
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger className="h-11"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ESTADOS.map((e) => (
                  <SelectItem key={e} value={e}>{e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Tipo de Documento */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Tipo de Documento</Label>
            <Select value={tipoDocumento} onValueChange={(v) => { setTipoDocumento(v); if (v !== "Otro") setTipoOtroText(""); }}>
              <SelectTrigger className="h-11"><SelectValue placeholder="Seleccionar tipo…" /></SelectTrigger>
              <SelectContent>
                {TIPO_DOCUMENTO_OPTIONS.map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
                <SelectItem value="Otro">Otro</SelectItem>
              </SelectContent>
            </Select>
            {tipoDocumento === "Otro" && (
              <Input
                className="h-11 mt-1.5"
                placeholder="Especificar tipo…"
                value={tipoOtroText}
                onChange={(e) => setTipoOtroText(e.target.value)}
              />
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Embarque (DO) asociado</Label>
            <EmbarqueSelectField value={embarqueId} onValueChange={setEmbarqueId} className="h-11" />
          </div>

          {/* Editable Fields — filtered & ordered by profile */}
          {editableFields.map((f) => (
            <div key={f.key} className="space-y-1.5">
              <Label className={cn("text-xs text-muted-foreground", f.key === "moneda" && isMonedaMissing && "text-destructive font-medium")}>
                {f.label}{f.key === "moneda" && " *"}
              </Label>
              {f.key === "moneda" ? (
                <>
                  <Select value={values[f.key] ?? ""} onValueChange={(val) => setValues((prev) => ({ ...prev, [f.key]: val }))}>
                    <SelectTrigger className={cn("h-11", isMonedaMissing && "border-destructive ring-destructive/30 ring-2")}>
                      <SelectValue placeholder="Seleccionar moneda…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="USD">USD - Dólar Estadounidense</SelectItem>
                      <SelectItem value="COP">COP - Peso Colombiano</SelectItem>
                      <SelectItem value="EUR">EUR - Euro</SelectItem>
                      <SelectItem value="CNY">CNY - Yuan Chino</SelectItem>
                    </SelectContent>
                  </Select>
                  {isMonedaMissing && (
                    <p className="text-[11px] text-destructive">La moneda es obligatoria para el cálculo de costos.</p>
                  )}
                </>
              ) : (
                <>
                  <Input
                    className={cn("h-11")}
                    type={f.type === "number" ? "number" : "text"}
                    inputMode={f.type === "number" ? "decimal" : "text"}
                    value={values[f.key] ?? ""}
                    onChange={(e) => setValues((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    onBlur={() => {
                      if (f.type === "number") {
                        setValues((prev) => ({ ...prev, [f.key]: String(parseFloat(prev[f.key]) || 0) }));
                      }
                    }}
                  />
                </>
              )}
            </div>
          ))}

          {/* Total Factura (financiero, derivado) — solo cuando hay items */}
          {showInventorySection && profile === "financial" && (
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground flex items-center gap-1">
                Total Factura
                <Lock className="h-3 w-3 text-muted-foreground/50" />
              </Label>
              <Input
                className={cn("h-11 bg-muted/50 font-mono tabular-nums")}
                value={derivedTotals.total_factura.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                disabled
              />
            </div>
          )}

          {/* ── Datos Logísticos — sección dedicada ───────────────────────── */}
          <div className="border-l-2 border-primary/30 pl-3 pt-2 space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
                <Package className="h-3.5 w-3.5 text-primary" />
                Datos Logísticos
              </h3>
              {logisticsLocked && (
                <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                  <Lock className="h-3 w-3" /> Derivados de items
                </span>
              )}
            </div>

            {netoMayorQueBruto && (
              <Alert className="border-amber-500/60 bg-amber-500/10 py-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                <AlertDescription className="text-xs text-amber-700 dark:text-amber-300">
                  El peso neto ({pesoNetoNum?.toLocaleString()} kg) es mayor que el peso bruto ({pesoBrutoNum?.toLocaleString()} kg). Verifique los valores antes de guardar.
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {/* Total Piezas */}
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Boxes className="h-3 w-3" /> Total Piezas
                  {logisticsLocked && <Lock className="h-2.5 w-2.5" />}
                </Label>
                {logisticsLocked ? (
                  <Input
                    className="h-10 bg-muted/50 font-mono tabular-nums"
                    value={derivedTotals.total_piezas.toLocaleString()}
                    disabled
                  />
                ) : (
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="1"
                    min="0"
                    className="h-10 font-mono tabular-nums"
                    placeholder={derivedTotals.total_piezas > 0 ? `Sugerido: ${derivedTotals.total_piezas.toLocaleString()}` : "—"}
                    value={values["total_piezas"] ?? ""}
                    onChange={(e) => setValues(prev => ({ ...prev, total_piezas: e.target.value }))}
                  />
                )}
              </div>

              {/* Peso Bruto */}
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Scale className="h-3 w-3" /> Peso Bruto (kg)
                  {logisticsLocked && <Lock className="h-2.5 w-2.5" />}
                </Label>
                {logisticsLocked ? (
                  <Input
                    className="h-10 bg-muted/50 font-mono tabular-nums"
                    value={derivedTotals.peso_bruto_kg.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    disabled
                  />
                ) : (
                  <Input
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min="0"
                    className={cn("h-10 font-mono tabular-nums", netoMayorQueBruto && "border-amber-500")}
                    placeholder={derivedTotals.peso_bruto_kg > 0 ? `Sugerido: ${derivedTotals.peso_bruto_kg.toLocaleString()}` : "—"}
                    value={values["peso_bruto_kg"] ?? ""}
                    onChange={(e) => setValues(prev => ({ ...prev, peso_bruto_kg: e.target.value }))}
                  />
                )}
              </div>

              {/* Peso Neto — siempre editable */}
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Scale className="h-3 w-3" /> Peso Neto (kg)
                </Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  className={cn("h-10 font-mono tabular-nums", netoMayorQueBruto && "border-amber-500")}
                  placeholder="—"
                  value={values["peso_neto_kg"] ?? ""}
                  onChange={(e) => setValues(prev => ({ ...prev, peso_neto_kg: e.target.value }))}
                />
              </div>

              {/* Volumen — siempre editable */}
              <div className="space-y-1">
                <Label className="text-[11px] text-muted-foreground flex items-center gap-1">
                  <Box className="h-3 w-3" /> Volumen (CBM)
                </Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.001"
                  min="0"
                  className="h-10 font-mono tabular-nums"
                  placeholder="—"
                  value={values["volumen_cbm"] ?? ""}
                  onChange={(e) => setValues(prev => ({ ...prev, volumen_cbm: e.target.value }))}
                />
              </div>
            </div>

            <p className="text-[10px] text-muted-foreground">
              Vacío = sin dato. Use 0 solo si declara explícitamente cero.
            </p>
          </div>


          {/* Gastos Extraídos — solo para documentos de gasto (BL, Guía Aérea, Doc Puerto, Póliza, Factura de Compras) */}
          {showExpensesSection && doc && (
            <div className="pt-2">
              <ExtractedExpensesSection documentoId={doc.id} variant="cards" />
            </div>
          )}
        </div>
        <DrawerFooter className="border-t border-border">
          <Button onClick={handleSaveClick} disabled={saving || isMonedaMissing} className="w-full h-12">
            {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            Guardar Cambios
          </Button>
          {isMonedaMissing && (
            <p className="text-[11px] text-destructive text-center animate-in fade-in slide-in-from-top-1 duration-200">
              Complete el campo Moneda para habilitar el guardado.
            </p>
          )}
          <DrawerClose asChild>
            <Button variant="outline" className="w-full">Cancelar</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>

      <AlertDialog open={showConversionDialog} onOpenChange={setShowConversionDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Convertir items a gastos?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2">
                <p>Estás cambiando este documento de <strong>mercancía</strong> a <strong>servicio</strong> ({pendingTipo}).</p>
                <p>Los <strong>{doc ? inventoryItems.filter(i => i.documento_id === doc.id).length : 0} items</strong> de inventario se archivarán y se convertirán en líneas de <strong>gasto prorrateable</strong> en el embarque.</p>
                <p className="text-amber-600 dark:text-amber-400 text-sm">⚠️ Esta acción es reversible solo restaurando manualmente desde audit logs.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel onClick={() => setShowConversionDialog(false)}>
              Cancelar cambio
            </AlertDialogCancel>
            <Button variant="outline" onClick={() => { setShowConversionDialog(false); performSave(false); }}>
              Solo cambiar tipo (mantener items)
            </Button>
            <AlertDialogAction onClick={() => { setShowConversionDialog(false); performSave(true); }}>
              Convertir a gastos
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Drawer>
  );
}
